// src/pages/Vouchers/RedeemEditPage.js
//
// Edit a single redeem entry.
// ==========================
//
// RESPONSIBILITIES
// ----------------
// This page edits ONE existing redeem-history row.
//
// UX rules preserved from the previous version:
//   - Keep the form visible when SAVE fails (HTTP 400 etc.) so the user can
//     correct the input.
//   - Show fatal load errors (failed to load entry) as a page-level error.
//   - Soft client-side guard: prevent submitting when amount >
//       (remaining + oldAmount).
//   - Server remains the source of truth; backend re-validates maxAllowed.
//
// NEW WORDING UPDATE
// ------------------
// This page now follows the same business wording pattern as the other
// voucher/package screens:
//
//   - category !== "Pakiet"  -> "Voucher"
//   - category === "Pakiet"  -> "Package"
//
// IMPORTANT:
// ----------
// The REDEEM DOMAIN FLOW itself remains shared.
// We only change user-facing wording here.
//
// WHY THIS IS CORRECT
// -------------------
// Packages still live in the shared voucher domain/table and are redeemed
// through the same backend logic, but from the operator's UI perspective
// they should no longer always be called "Voucher".
//
// Therefore this page:
//   - keeps the technical flow unchanged,
//   - but derives UI labels from voucher.category.
//
// DATA FLOW
// ---------
// 1) Load redeem entry by redeemId
// 2) Load voucher/package row for header + remaining balance
// 3) Build UI wording from voucher.category
// 4) Let the operator edit:
//      - reservation_id
//      - redeemed_amount
//      - noted
//      - comment
// 5) Submit update through updateRedeemEntry(...)
//

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  getRedeemEntry,
  updateRedeemEntry,
  getVoucherById, // helper for voucher/package header + remaining balance
} from "../../services/voucherService";
import "./RedeemEditPage.css";

// -----------------------------------------------------------------------------
// BUSINESS WORDING HELPERS
// -----------------------------------------------------------------------------
//
// We keep these helpers local on purpose.
// At this stage they are tiny, explicit, and safe.
// If later you want, they could be moved to a shared frontend util.
//
// Rule:
//   category === "Pakiet" -> package wording
//   otherwise             -> voucher wording
//
const PACKAGE_CATEGORY_VALUE = "Pakiet";

/**
 * getBusinessModeFromVoucherRow(voucher)
 *
 * Returns a lightweight UI/business mode identifier based on category.
 *
 * IMPORTANT:
 * This is NOT a backend enum and NOT a persistence value.
 * It only drives wording on this page.
 */
function getBusinessModeFromVoucherRow(voucher) {
  return voucher?.category === PACKAGE_CATEGORY_VALUE ? "package" : "voucher";
}

/**
 * getEntityUiText(voucher)
 *
 * Produces all human-facing labels/messages for this page.
 *
 * We keep this centralized so the page does not scatter
 * package/voucher string checks across multiple JSX fragments.
 */
function getEntityUiText(voucher, t) {
  const mode = getBusinessModeFromVoucherRow(voucher);
  const isPackage = mode === "package";

  const entityKey = isPackage ? "package" : "voucher";
  const entityLabel = t(`vouchers:entity.${entityKey}.singular`, {
    defaultValue: isPackage ? "Package" : "Voucher",
  });
  const entityLower = t(`vouchers:entity.${entityKey}.lowerSingular`, {
    defaultValue: entityLabel.toLowerCase(),
  });

  const i18nVars = { entity: entityLabel, entityLower };

  return {
    entityLabel,
    entityLower,

    pageTitle: t("vouchers:redeemEdit.pageTitle"),
    headerTitle: t("vouchers:redeemEdit.headerTitle", i18nVars),

    entityNumberLabel: t("vouchers:redeem.entityNumberLabel", i18nVars),
    remainingLabel: t("vouchers:redeemEdit.remainingLabel"),
    statusLabel: t("vouchers:redeemEdit.statusLabel"),

    reservationIdLabel: t("vouchers:redeem.reservationIdLabel"),
    reservationIdPlaceholder: t("vouchers:redeemEdit.reservationIdPlaceholder", {
      defaultValue: "Optional",
    }),

    amountLabel: t("vouchers:redeemEdit.amountLabel"),
    maxAllowedLabel: t("vouchers:redeemEdit.maxAllowedLabel"),

    notedLabel: t("vouchers:redeemEdit.notedLabel"),

    commentLabel: t("vouchers:redeemEdit.commentLabel"),
    commentPlaceholder: t("vouchers:redeemEdit.commentPlaceholder", i18nVars),

    saveButton: t("vouchers:redeemEdit.saveButton"),
    savingButton: t("vouchers:redeemEdit.savingButton"),
    cancelButton: t("common:actions.cancel"),

    amountInvalidError: t("vouchers:redeemEdit.amountInvalidError"),
    maxAllowedError: (maxAllowed) =>
      t("vouchers:redeemEdit.maxAllowedError", {
        maxAllowed: maxAllowed.toFixed(2),
      }),

    loadError: t("vouchers:redeemEdit.loadError"),
    saveError: t("vouchers:redeemEdit.saveError", i18nVars),
    saveSuccess: t("vouchers:redeemEdit.saveSuccess", i18nVars),
  };
}

export default function RedeemEditPage() {
  const navigate = useNavigate();
  const { redeemId } = useParams();
  const { t } = useTranslation(["common", "vouchers"]);
  // i18n note:
  // This page edits an existing redeem row. The database model is unchanged;
  // translations only affect labels, hints and validation messages.

  const location = useLocation();
  const { state } = location; // may contain { voucherId }

  const backToOverviewPath =
  state?.returnTo || "/vouchers/overview";  

  // ---------------------------------------------------------------------------
  // Page load states
  // ---------------------------------------------------------------------------
  //
  // loadError:
  //   fatal -> we cannot render a meaningful form
  //
  // saveError:
  //   non-fatal -> keep the form visible so the user can correct input
  //
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  // ---------------------------------------------------------------------------
  // Loaded data
  // ---------------------------------------------------------------------------
  const [voucher, setVoucher] = useState(null);
  const [entry, setEntry] = useState(null); // original redeem entry

  // ---------------------------------------------------------------------------
  // Form fields
  // ---------------------------------------------------------------------------
  //
  // We keep amount as a STRING in local state because:
  //   - user types it in an <input type="number">
  //   - intermediate values should remain editable
  //   - final conversion happens in onSave()
  //
  const [reservationId, setReservationId] = useState("");
  const [amount, setAmount] = useState("");
  const [noted, setNoted] = useState(false);
  const [comment, setComment] = useState("");

  // ---------------------------------------------------------------------------
  // Business wording derived from loaded voucher/package row
  // ---------------------------------------------------------------------------
  //
  // If voucher is not loaded yet, we gracefully fall back to voucher wording.
  //
  const uiText = useMemo(() => getEntityUiText(voucher, t), [voucher, t]);

  // ---------------------------------------------------------------------------
  // Derived: header line for voucher/package summary
  // ---------------------------------------------------------------------------
  //
  // Example:
  //   "K/25/10/001 — Remaining: 120.00 PLN"
  //
  const voucherHeader = useMemo(() => {
    if (!voucher) return "";

    const rem = Number(voucher.remaining_balance);
    return `${voucher.voucher_no} — ${uiText.remainingLabel} ${Number(rem).toFixed(
      2
    )} PLN`;
  }, [voucher, uiText.remainingLabel]);

  // ---------------------------------------------------------------------------
  // Derived: maxAllowed = remaining + oldAmount
  // ---------------------------------------------------------------------------
  //
  // Why this formula?
  // -----------------
  // When editing an existing redeem entry, the operator is allowed to increase
  // the redeemed amount only up to:
  //
  //   current remaining balance + amount already consumed by this same entry
  //
  // This mirrors backend validation and gives the operator a fast client-side
  // hint before the request is sent.
  //
  const maxAllowed = useMemo(() => {
    if (!voucher || !entry) return undefined;

    const remaining = Number(voucher.remaining_balance) || 0;
    const oldAmount = Number(entry.redeemed_amount) || 0;

    return Math.round((remaining + oldAmount) * 100) / 100;
  }, [voucher, entry]);

  // ---------------------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------------------
  //
  // Flow:
  //   1) load redeem entry by redeemId
  //   2) populate editable form fields
  //   3) load voucher/package row for contextual header + remaining balance
  //
  // We intentionally keep the voucher fetch separate because:
  //   - redeem entry may load successfully even if the extra voucher fetch fails
  //   - the page structure is clearer this way
  //
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadError("");
      setSaveError("");
      setSuccess("");
      setLoading(true);

      try {
        const redeem = await getRedeemEntry(redeemId);
        if (cancelled) return;

        setEntry(redeem);
        setReservationId(redeem.reservation_id || "");
        setAmount(String(Number(redeem.redeemed_amount).toFixed(2)));
        setNoted(Boolean(redeem.noted));
        setComment(redeem.comment || "");

        // Load voucher/package for header + remaining balance.
        // We prefer location.state.voucherId when passed from navigation,
        // otherwise fall back to redeem.voucher_id from the API row.
        const voucherId = state?.voucherId ?? redeem.voucher_id;

        if (voucherId) {
          const loadedVoucher = await getVoucherById(voucherId);
          if (!cancelled) setVoucher(loadedVoucher);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.response?.data?.message || uiText.loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [redeemId, state?.voucherId, uiText.loadError]);

  // ---------------------------------------------------------------------------
  // Save handler
  // ---------------------------------------------------------------------------
  //
  // Keeps current UX contract:
  //   - fatal validation issues stay on page
  //   - on success we go back to overview
  //
  const onSave = async () => {
    setSaveError("");
    setSuccess("");

    const amountNumber = Number(amount);

    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      setSaveError(uiText.amountInvalidError);
      return;
    }

    // Soft client-side guard – backend still remains authoritative.
    if (typeof maxAllowed === "number" && amountNumber > maxAllowed) {
      setSaveError(uiText.maxAllowedError(maxAllowed));
      return;
    }

    setSaving(true);

    try {
      const payload = {
        redeemed_amount: amountNumber,
        noted: !!noted,
        reservation_id: reservationId || null,
        comment: comment || null,
      };

      const response = await updateRedeemEntry(entry.voucher_id, entry.id, payload);

      setSuccess(response?.message || uiText.saveSuccess);

      // Keep current flow:
      // after a successful edit we return to overview.
      // After a successful edit, return to the originating overview state.
      // If this page was opened directly or from Command Search, fall back to the
      // normal Voucher Overview route.
      navigate(backToOverviewPath, { replace: true });
    } catch (e) {
      // Non-fatal: keep the form visible.
      setSaveError(e?.response?.data?.message || uiText.saveError);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    navigate(backToOverviewPath);
  };

  return (
    <div
      className="redeem-edit-page"
      role="main"
      aria-label={uiText.headerTitle}
      data-mode={getBusinessModeFromVoucherRow(voucher)}
    >
      <h1>{uiText.headerTitle}</h1>

      {loading ? (
        <div className="rep-skeleton" aria-busy="true">
          {t("common:common.loading")}
        </div>
      ) : loadError ? (
        // Fatal page-level error (e.g. redeem entry not found)
        <div className="rep-msg rep-error">{loadError}</div>
      ) : (
        <>
          {voucher && (
            <div className="rep-card rep-voucher">
              <div className="rep-voucher-line">
                <strong>{uiText.entityNumberLabel}</strong> {voucherHeader}
              </div>
              <div>
                <strong>{uiText.statusLabel}</strong> {voucher.status}
              </div>
            </div>
          )}

          {/* Non-fatal save error: visible, but form stays editable */}
          {saveError && <div className="rep-msg rep-error">{saveError}</div>}
          {success && <div className="rep-msg rep-success">{success}</div>}

          <div className="rep-card">
            <div className="rep-row">
              <label>{uiText.reservationIdLabel}</label>
              <input
                type="text"
                value={reservationId}
                onChange={(e) => setReservationId(e.target.value)}
                placeholder={uiText.reservationIdPlaceholder}
                autoComplete="off"
              />
            </div>

            <div className="rep-row">
              <label>{uiText.amountLabel}</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
              />
              {typeof maxAllowed === "number" && (
                <div className="rep-hint">
                  {uiText.maxAllowedLabel} {maxAllowed.toFixed(2)} PLN
                </div>
              )}
            </div>

            <div className="rep-row">
              <label className="rep-inline">
                <input
                  type="checkbox"
                  checked={noted}
                  onChange={(e) => setNoted(e.target.checked)}
                />
                <span>{uiText.notedLabel}</span>
              </label>

              <span
                className={`status-chip ${noted ? "status-yes" : "status-no"}`}
                style={{ marginLeft: 8 }}
              >
                {noted ? t("common:common.yes") : t("common:common.no")}
              </span>
            </div>

            <div className="rep-row">
              <label>{uiText.commentLabel}</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={uiText.commentPlaceholder}
                rows={3}
              />
            </div>
          </div>

          <div className="rep-actions">
            <button
              className="btn btn-primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? uiText.savingButton : uiText.saveButton}
            </button>

            <button
              className="btn btn-ghost"
              onClick={onCancel}
              disabled={saving}
            >
              {uiText.cancelButton}
            </button>
          </div>
        </>
      )}
    </div>
  );
}